import Input from "@app/components/input";
import Pagination from "@app/components/pagination";
import {
  DEFAULT_PAGE,
  DEFAULT_TOTAL_PAGES,
  DeviceStatus,
  DeviceType,
} from "@app/constants";
import DeviceService from "@app/services/http/device.service";
import StatisticalService from "@app/services/http/statistical.service";
import SocketService from "@app/services/socket.service";
import { Device, Statistical } from "@app/types";
import { formatDate } from "@app/utils/util";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import clsx from "clsx";
import { ChangeEvent, useEffect, useState } from "react";
import { FaDotCircle } from "react-icons/fa";
import { debounceTime, Subject, Subscription } from "rxjs";

function StatisticalManagement() {
  const [statistical, setStatistical] = useState<Statistical[]>([]);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [page, setPage] = useState(DEFAULT_PAGE);

  // Lấy ngày hiện tại và định dạng YYYY-MM-DD
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);

  const [device, setDevice] = useState<Device>();

  const [update, forceUpdate] = useForceUpdate();

  const { subscribeUntilDestroy } = useObservable();

  //on socket
  //on message from server
  const subscription = new Subscription();

  //on
  useEffect(() => {
    //on join
    const subDeviceJoin = SocketService.onDeviceJoin().subscribe((data) => {
      if (data === device?.device_id) {
        forceUpdate();
      }
    });

    subscription.add(subDeviceJoin);

    //on disconnect
    const subDeviceDisconnect = SocketService.onDeviceDisconnect().subscribe(
      (data) => {
        if (data === device?.device_id) {
          forceUpdate();
        }
      }
    );

    subscription.add(subDeviceDisconnect);

    //on working
    const subDeviceWorking = SocketService.onDeviceWorking().subscribe(
      (data) => {
        if (data === device?.device_id) {
          forceUpdate();
        }
      }
    );

    subscription.add(subDeviceWorking);

    //on done
    const subDeviceDone = SocketService.onDeviceDone().subscribe((data) => {
      if (data === device?.device_id) {
        forceUpdate();
      }
    });

    subscription.add(subDeviceDone);
  }, [subscription]);

  useEffect(() => () => subscription.unsubscribe(), [subscription]);

  useEffect(() => {
    // Lấy URL hiện tại
    const searchParams = new URLSearchParams(window.location.search);

    // Lấy các tham số từ URL
    const id = searchParams.get("_id") || "";

    subscribeUntilDestroy(DeviceService.getDeviceByID(id), (device) => {
      setDevice(device);
    });
  }, [update]);

  useEffect(() => {
    device &&
      subscribeUntilDestroy(
        StatisticalService.getStatistical(device?._id, fromDate, page),
        (res) => {
          if (res.data.length) {
            setStatistical(res.data);
            setTotalPages(res.total_page);
          } else {
            setStatistical([]);
            setTotalPages(DEFAULT_TOTAL_PAGES);
          }
        }
      );
  }, [device, fromDate, page]);

  const onPageChange = (page: number) => {
    setPage(page);
  };

  const fromDateSubject = new Subject<string>();

  useEffect(() => {
    fromDateSubject.pipe(debounceTime(300)).subscribe((data) => {
      setFromDate(data);
    });
  }, [fromDateSubject]);

  const onFromDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    fromDateSubject.next(e.target.value);
  };

  return (
    <div className="w-full mx-auto mt-4 pb-4">
      <div className="ml-4">
        <h1>Bạn đang xem thống kê của thiết bị</h1>
        <table>
          <tr>
            <th className="p-3 text-left w-52">Online</th>
            <th className="p-3 text-left w-52">Hoạt động</th>
            <th className="p-3 text-left w-52">Vị Trí</th>
            <th className="p-3 text-left w-72">Tên tòa nhà</th>
            <th className="p-3 text-left w-72">Tên thiết bị</th>
            <th className="p-3 text-left w-72">Nhà mạng</th>
            <th className="p-3 text-left w-72">Số điện thoại</th>
            <th className="p-3 text-left w-72">Emei Device</th>
            <th className="p-3 text-left w-52">Loại máy</th>
            <th className="p-3 text-left w-52">Trạng Thái</th>
          </tr>
          <tr>
            <td className="p-3">
              {device?.is_connecting ? (
                <FaDotCircle className="text-green-500" />
              ) : (
                <FaDotCircle className="text-gray-500" />
              )}
            </td>
            <td className="p-3">
              {device?.is_working ? (
                <span className="text-green-500">Đang làm việc</span>
              ) : (
                <span className="text-red-500">Xong</span>
              )}
            </td>
            <td className="p-3">{device?.location}</td>
            <td className="p-3">{device?.building_name}</td>
            <td className="p-3">{device?.device_name}</td>
            <td className="p-3">{device?.network_provider}</td>
            <td className="p-3">{device?.phone}</td>
            <td className="p-3">{device?.device_id}</td>
            <td className="p-3">{device?.type}</td>
            <td
              className={clsx(
                `p-3 ${
                  device?.status === DeviceStatus.ACTIVE
                    ? "text-green-500"
                    : "text-red-500"
                }`
              )}
            >
              {device?.status}
            </td>
          </tr>
        </table>
      </div>
      <div className="px-2 overflow-x-auto mt-2">
        <div>
          <span>
            Tổng {device?.type === DeviceType.DOWNLOAD ? "GB" : "Erl"} đã{" "}
            {device?.type}
            {": "}
          </span>
          <span>
            {statistical.reduce((pre, curr) => pre + curr.total, 0).toFixed(2)}
          </span>
        </div>
        <div className="flex items-center gap-2 m-2">
          <div className="font-bold">Chọn Ngày Thống Kê</div>
          <Input
            type="date"
            className="mt-0 max-w-[250px]"
            placeholder="Nhập Ngày"
            value={fromDate}
            onChange={onFromDateChange}
          />
        </div>
        <div className="inline-block min-w-full shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full leading-normal">
            <thead className="text-white">
              <tr className="border-b-2 border-gray-200 bg-[#409EFF] text-sm">
                <th className="p-3 text-center w-6">
                  Tổng {device?.type === DeviceType.DOWNLOAD ? "GB" : "Erl"}
                </th>
                <th className="p-3 text-center w-6">Vị Trí</th>
                <th className="p-3 text-center w-6">Thời gian</th>
              </tr>
            </thead>
            <tbody>
              {!!statistical.length &&
                statistical.map((item) => (
                  <tr
                    key={item._id}
                    className="border-b border-gray-200 text-sm hover:bg-[#51b83c2c]"
                  >
                    <td className="p-3 text-center">{item.total.toFixed(2)}</td>
                    <td className="p-3 text-center">{item.location}</td>
                    <td className="p-3 text-center">
                      {formatDate(item.createdAt)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!statistical.length && (
            <div className="flex justify-center">Không có dữ liệu</div>
          )}
        </div>
      </div>
      {totalPages > 0 && (
        <div className="flex justify-center mt-3">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}

export default StatisticalManagement;
